/**
 * Displays paginated list of user orders in card format.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Immutable from 'immutable';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { fetchOrders } from '../../../../../store/actions/Orders';
import { ROUTE_ACCOUNT_ORDERS } from '../../../../../config/routes';
import type { Order } from '../../../../../types/orders';
import OrderCard from './OrderCard';

import './style.scss';

const ORDERS_PER_PAGE = 5;

const OrdersList: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);

  const ordersList = useSelector<ImmutableRootState<Immutable.List<Order>>, Immutable.List<Order>>(
    (state) => state.getIn(['orders', 'list'], Immutable.List()),
  );

  const hasMore = useSelector<ImmutableRootState<boolean>, boolean>(
    (state) => state.getIn(['orders', 'pagination', 'hasMore'], false),
  );

  useEffect(() => {
    const loadOrders = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        await dispatch(fetchOrders(ORDERS_PER_PAGE, 0, false));
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleLoadMore = useCallback(async () => {
    try {
      setIsLoadingMore(true);
      await dispatch(fetchOrders(ORDERS_PER_PAGE, ordersList.size, true));
    } catch {
      // Silently fail - user can retry
    } finally {
      setIsLoadingMore(false);
    }
  }, [dispatch, ordersList.size]);

  const handleViewDetails = useCallback((orderId: number) => {
    history.push(`${ROUTE_ACCOUNT_ORDERS}/${orderId}`);
  }, [history]);

  const handleManageSeats = useCallback((orderId: number) => {
    history.push(`${ROUTE_ACCOUNT_ORDERS}/${orderId}/licenses`);
  }, [history]);

  if (isLoading) {
    return (
      <div className="OrdersList OrdersList--loading">
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="OrdersList OrdersList--error">
        <p>{t('mijn_vnl.orders.load_error')}</p>
      </div>
    );
  }

  const orders = ordersList.toJS() as Order[];

  if (orders.length === 0) {
    return (
      <div className="OrdersList OrdersList--empty">
        <p>{t('mijn_vnl.orders.empty')}</p>
      </div>
    );
  }

  return (
    <div className="OrdersList">
      <div className="OrdersList__grid">
        {orders.map((order: Order) => (
          <OrderCard
            key={order.order_id}
            order={order}
            onViewDetails={handleViewDetails}
            onManageSeats={handleManageSeats}
          />
        ))}
      </div>

      {hasMore && (
        <Button
          className="Button Button--inverted OrdersList__load-more"
          onClick={handleLoadMore}
          disabled={isLoadingMore}
        >
          {isLoadingMore ? t('mijn_vnl.orders.loading_more') : t('mijn_vnl.orders.load_more')}
          {!isLoadingMore && (
            <i className="icon icon-arrow-down" aria-hidden="true" />
          )}
        </Button>
      )}
    </div>
  );
};

export default OrdersList;
